import { DataGrid } from "@mui/x-data-grid";
import { forwardRef, useImperativeHandle } from "react";
import { esES } from "@mui/x-data-grid/locales";
import { ACTION_TYPES } from "../../../constants";
import {
  buildAuditLogFilterParams,
  formatAuditLogChange,
} from "../../../utils/helpers";
import useUserActionLogs from "../../../hooks/useUserActionLogs";

const UserActions = forwardRef((_, ref) => {
  const {
    rows,
    rowCount,
    loading,
    pagination,
    setPagination,
    filterModel,
    setFilterModel,
  } = useUserActionLogs();

  const columns = [
    {
      field: "created_at",
      headerName: "Fecha",
      flex: 1,
      minWidth: 180,
      type: "dateTime",
      valueFormatter: (params) => {
        const date = new Date(params);
        return new Intl.DateTimeFormat("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }).format(date);
      },
    },
    {
      field: "action_type",
      headerName: "Tipo de acción",
      flex: 1,
      minWidth: 160,
      type: "singleSelect",
      valueOptions: Object.entries(ACTION_TYPES).map(([value, label]) => ({
        value,
        label,
      })),
      valueFormatter: (params) => ACTION_TYPES[params] ?? params,
    },
    {
      field: "action_description",
      headerName: "Descripción",
      flex: 1,
      minWidth: 220,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "changes",
      headerName: "Cambios",
      width: 450,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        !Array.isArray(params.row.changes) || params.row.changes.length === 0
          ? "-"
          : params.row.changes.map((change, index) => {
              const formatted = formatAuditLogChange(change);

              return (
                <div
                  key={`${change?.property ?? "change"}-${index}`}
                  className="flex gap-2 font-semibold mb-2"
                >
                  {formatted.isPlainText ? (
                    <span>{formatted.text}</span>
                  ) : (
                    <>
                      <span>{formatted.label ?? "-"}:</span>
                      <div>
                        <span>{formatted.originalValue}</span>
                        <span>→</span>
                        <span>{formatted.changedValue}</span>
                      </div>
                    </>
                  )}
                </div>
              );
            }),
    },
    { field: "username", headerName: "Usuario", flex: 1, minWidth: 140 },
  ];

  useImperativeHandle(ref, () => ({
    getExportParams: () => buildAuditLogFilterParams(filterModel),
  }));

  return (
    <div className="w-full h-[600px]">
      <DataGrid
        columns={columns}
        rows={rows}
        pagination
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[10, 25, 50, 100]}
        rowCount={rowCount}
        loading={loading}
        getRowHeight={() => "auto"}
        getEstimatedRowHeight={() => 200}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "hsl(240 4.8% 95.9%)",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: "white",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "hsl(240 4.8% 95.9%)",
          },
          "& .MuiDataGrid-cell": {
            py: 1,
          },
        }}
      />
    </div>
  );
});

export default UserActions;
